import React, { memo } from "react";
import _ from "lodash";
import moment from "moment";
import { Date } from "components/Shared";
import { translate } from "lib";

interface Props {
	measurements?: tcpinpoint.AreaMeasurement[];
}

// Every area restatement Yardi has sent for a unit, oldest first.
//
// One row per UnitAreaID (tenancies.area_measurements), which is why the React
// key is that and not the date: two restatements can carry the same as_of_date.
//
// Yardi restates a unit's areas over time and stamps each restatement with an
// AsOfDate that CAN BE IN THE FUTURE. Everywhere else in the app shows only the
// measurement in force today (Tenancy#effective_area_measurement), so this table
// is the only place the history — and anything dated ahead — is visible.
//
// Renders nothing when the unit has no measurements, so it can be dropped into
// any layout without an emptiness check around it.
const AreaMeasurements = ({ measurements }: Props) => {
	// Sorted again rather than trusted: the API returns the series ascending
	// (Tenancy#area_measurements), but this is cheap and keeps the component
	// honest wherever its data comes from.
	const rows = _.sortBy(measurements || [], "as_of_date");

	if (_.isEmpty(rows)) return null;

	// The row whose numbers the unit actually reports today — the latest
	// as_of_date that is not in the future, mirroring
	// Tenancy#effective_area_measurement. Undefined when every restatement is
	// future-dated, in which case the unit has no area yet and nothing is starred.
	//
	// `moment()` rather than `new Date().toISOString()`: the latter is UTC, so a
	// user in Dubai or Sydney would flip the effective row hours early or late.
	// ISO dates compare correctly as strings.
	const today = moment().format("YYYY-MM-DD");
	const effective = _.findLast(rows, row => row.as_of_date <= today);

	return (
		<React.Fragment>
			<h4>{translate("area_measurements")}</h4>
			<table className="table" style={{ width: "100%" }}>
				<thead>
					<tr>
						<th>{translate("unit_area_id")}</th>
						<th>{translate("as_of_date")}</th>
						<th>{translate("lease_area_sqm")}</th>
						<th>{translate("lease_area_sqft")}</th>
						<th>{translate("actual_area_sqm")}</th>
						<th>{translate("actual_area_sqft")}</th>
					</tr>
				</thead>
				<tbody>
					{_.map(rows, measurement => (
						<tr key={measurement.unit_area_id}>
							{/* The * marks the measurement whose numbers the unit reports
							    today. It sits on the ID — the row's identity — rather than on
							    the date, since the date is not what makes a row unique.
							    Marked rather than spelled out; the title carries the
							    explanation. The fixed-width span is on every row, so the IDs
							    stay in a straight column whether starred or not. */}
							<td>
								<span style={{ display: "inline-block", width: "1em" }}>
									{measurement === effective && (
										<strong title={translate("area_measurements_effective")}>*</strong>
									)}
								</span>
								{measurement.unit_area_id}
							</td>
							<td>
								<Date date={measurement.as_of_date} showTime={false} />
							</td>
							<td>{measurement.lease_area_sqm}</td>
							<td>{measurement.lease_area_sqft}</td>
							<td>{measurement.actual_area_sqm}</td>
							<td>{measurement.actual_area_sqft}</td>
						</tr>
					))}
				</tbody>
			</table>
		</React.Fragment>
	);
};

export default memo(AreaMeasurements);
